import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { produce } from "immer";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import TertiaryButton from "@components/buttons/TertiaryButton";
import BaseErrors from "@components/fields/BaseErrors";
import BooleanRadioButtonGroup from "@components/fields/BooleanRadioButtonGroup";
import CareProfileInput from "@components/fields/CareProfileInput";
import Select from "@components/fields/Select";
import FileResource from "@components/FileResource";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import CareProfileRollupItem from "@components/forms/careProfile/CareProfileRollupItem";
import Form from "@components/forms/Form";
import LegalDocumentForm from "@components/forms/LegalDocumentForm";
import Modal, { ModalElement } from "@components/Modal";
import BodyCopy from "@components/typography/BodyCopy";
import useOpener from "@hooks/useOpener";
import {
  CareProfileConservatorshipStatusEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileGuardianTypeEnum,
  CareProfileImmigrationStatusEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  CareProfileUseCaseEnum,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery,
  LegalDocumentKindEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import {
  optionsForBoolean,
  optionsForConservatorshipStatus,
  optionsForGuardianType,
  optionsForImmigrationStatus
} from "@utils/optionUtils";

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

type FormData = {
  conservatorshipDetails?: string | null;
  conservatorshipStatus?: CareProfileConservatorshipStatusEnum | null;
  guardianType?: CareProfileGuardianTypeEnum | null;
  immigrationStatus?: CareProfileImmigrationStatusEnum | null;
  legalHelpNeeded?: boolean | null;
};

type LegalDocument = GetJourneyWithCareProfileQuery["journey"]["legalDocuments"][number];

const CareProfileLegalForm = ({ careProfileFieldRules, journey }: Props) => {
  const intl = useIntl();

  const legalDocumentOpener = useOpener();
  const guardianLegalDocumentOpener = useOpener();
  const legalDocumentModalRef = useRef<ModalElement>(null);
  const guardianLegalDocumentModalRef = useRef<ModalElement>(null);
  const [editingLegalDocument, setEditingLegalDocument] = useState<LegalDocument>();
  const [editingGuardianLegalDocument, setEditingGuardianLegalDocument] = useState<LegalDocument>();

  const careProfile = journey.careProfile;

  const defaultValues: FormData = {
    conservatorshipDetails: careProfile.conservatorshipDetails ?? null,
    conservatorshipStatus: careProfile.conservatorshipStatus ?? null,
    guardianType: careProfile.guardianType ?? null,
    immigrationStatus: careProfile.immigrationStatus ?? null,
    legalHelpNeeded: careProfile.legalHelpNeeded ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      conservatorshipDetails: yup.string().nullable(),
      conservatorshipStatus: yup.string().nullable().oneOf(Object.values(CareProfileConservatorshipStatusEnum)),
      guardianType: yup.string().nullable().oneOf(Object.values(CareProfileGuardianTypeEnum)),
      immigrationStatus: yup.string().nullable().oneOf(Object.values(CareProfileImmigrationStatusEnum)),
      legalHelpNeeded: yup.boolean().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    const valuesWithoutErrors = produce(form.getValues(), draftValues => {
      Object.keys(form.formState.errors).forEach(field => {
        delete draftValues[field as keyof FormData];
      });
    });

    setBaseErrors([]);
    return updateCareProfile({
      variables: {
        ...valuesWithoutErrors,
        id: journey.careProfile.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.careProfile"
      });
    });
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  useEffect(() => {
    const debouncedSubmit = debounce(submitCareProfile, 2000);
    const subscription = form.watch(() => form.handleSubmit(debouncedSubmit, debouncedSubmit)());
    return () => subscription.unsubscribe();
  }, [form, submitCareProfile]);

  const conservatorshipStatusOptions = optionsForConservatorshipStatus(intl);
  const booleanOptions = optionsForBoolean(intl);
  const guardianTypeOptions = optionsForGuardianType(intl);
  const immigrationStatusOptions = optionsForImmigrationStatus(intl);

  const conservatorshipStatus = form.watch("conservatorshipStatus");

  const detailsForLegalDocument = (legalDocument: LegalDocument) => [
    {
      labelKey: "models.client.legalDocument.status",
      value: intl.formatMessage({ id: `models.legalDocument.statusOptions.${legalDocument.status}` })
    },
    { labelKey: "pages.legalDocuments.list.storageLocation", value: legalDocument.storageLocation },
    { labelKey: "pages.legalDocuments.list.updateDate", value: legalDocument.updateDate },
    { labelKey: "pages.legalDocuments.list.notes", value: legalDocument.notes }
  ];

  const guardianLegalDocumentKinds = [LegalDocumentKindEnum.GUARDIAN_TRUST, LegalDocumentKindEnum.GUARDIAN_WILL];

  const legalDocumentKindOptions = Object.values(LegalDocumentKindEnum).filter(
    kind =>
      !guardianLegalDocumentKinds.includes(kind) &&
      !journey.legalDocuments.map(legalDocument => legalDocument.kind).includes(kind)
  );

  const guardianLegalDocumentKindOptions = guardianLegalDocumentKinds.filter(
    kind => !journey.legalDocuments.map(legalDocument => legalDocument.kind).includes(kind)
  );

  return (
    <>
      <Form hookForm={form}>
        <BaseErrors errors={baseErrors} />
        <div className={styles.formSectionRow}>
          <div className={styles.formControlsColumn}>
            <div className={styles.rollupSection}>
              <BodyCopy>
                <FormattedMessage
                  id="forms.careProfileNew.legalDocumentsLabel"
                  values={{
                    count: journey.legalDocuments.filter(doc => !guardianLegalDocumentKinds.includes(doc.kind)).length
                  }}
                />
              </BodyCopy>
              {journey.legalDocuments
                .filter(doc => !guardianLegalDocumentKinds.includes(doc.kind))
                .map(legalDocument => (
                  <CareProfileRollupItem
                    details={detailsForLegalDocument(legalDocument)}
                    item={legalDocument}
                    key={legalDocument.id}
                    onEdit={() => {
                      setEditingLegalDocument(legalDocument);
                      legalDocumentOpener.open();
                    }}
                    title={intl.formatMessage({ id: `models.legalDocument.kindOptions.${legalDocument.kind}` })}
                  >
                    {legalDocument.fileResources.map(fr => (
                      <div key={fr.id}>
                        <FileResource fileResource={fr} />
                      </div>
                    ))}
                  </CareProfileRollupItem>
                ))}
            </div>

            {legalDocumentKindOptions.length > 0 && (
              <TertiaryButton
                icon="add"
                onClick={() => {
                  setEditingLegalDocument(undefined);
                  legalDocumentOpener.open();
                }}
                textKey="dictionary.new"
              />
            )}

            {[CareProfileUseCaseEnum.CHILD, CareProfileUseCaseEnum.INFANT].includes(
              journey.careProfile.useCase as CareProfileUseCaseEnum
            ) && (
              <>
                <div className={styles.rollupSection}>
                  <BodyCopy>
                    <FormattedMessage
                      id="forms.careProfileNew.guardianLegalDocumentsLabel"
                      values={{
                        count: journey.legalDocuments.filter(doc => guardianLegalDocumentKinds.includes(doc.kind))
                          .length
                      }}
                    />
                  </BodyCopy>
                  {journey.legalDocuments
                    .filter(doc => guardianLegalDocumentKinds.includes(doc.kind))
                    .map(legalDocument => (
                      <CareProfileRollupItem
                        details={detailsForLegalDocument(legalDocument)}
                        item={legalDocument}
                        key={legalDocument.id}
                        onEdit={() => {
                          setEditingGuardianLegalDocument(legalDocument);
                          guardianLegalDocumentOpener.open();
                        }}
                        title={intl.formatMessage({ id: `models.legalDocument.kindOptions.${legalDocument.kind}` })}
                      >
                        {legalDocument.fileResources.map(fr => (
                          <div key={fr.id}>
                            <FileResource fileResource={fr} />
                          </div>
                        ))}
                      </CareProfileRollupItem>
                    ))}
                </div>
                {guardianLegalDocumentKindOptions.length > 0 && (
                  <TertiaryButton
                    icon="add"
                    onClick={() => {
                      setEditingLegalDocument(undefined);
                      guardianLegalDocumentOpener.open();
                    }}
                    textKey="dictionary.new"
                  />
                )}
              </>
            )}
          </div>
          <div className={styles.formControlsColumn}>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.CONSERVATORSHIP_STATUS}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="conservatorshipStatus"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.conservatorshipStatus}
                label={intl.formatMessage({ id: "forms.careProfileNew.conservatorshipStatusLabel" })}
                name="conservatorshipStatus"
                options={conservatorshipStatusOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              condition={conservatorshipStatus === CareProfileConservatorshipStatusEnum.CONSERVED}
              fieldName={CareProfileFieldNameEnum.CONSERVATORSHIP_DETAILS}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="conservatorshipDetails"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <CareProfileInput<FormData>
                error={form.formState.errors.conservatorshipDetails}
                label={intl.formatMessage({ id: "forms.careProfileNew.conservatorshipDetailsLabel" })}
                register={form.register("conservatorshipDetails")}
                spellcheck
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.GUARDIAN_TYPE}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="guardianType"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.guardianType}
                label={intl.formatMessage({ id: "forms.careProfileNew.guardianTypeLabel" })}
                name="guardianType"
                options={guardianTypeOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.IMMIGRATION_STATUS}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="immigrationStatus"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.immigrationStatus}
                label={intl.formatMessage({ id: "forms.careProfileNew.immigrationStatusLabel" })}
                name="immigrationStatus"
                options={immigrationStatusOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.LEGAL_HELP_NEEDED}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="legalHelpNeeded"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <BooleanRadioButtonGroup
                allowDeselection
                control={form.control}
                error={form.formState.errors.legalHelpNeeded}
                label={intl.formatMessage({ id: "forms.careProfileNew.legalHelpNeededLabel" })}
                name="legalHelpNeeded"
                options={booleanOptions}
              />
            </CareProfileFormCondition>
          </div>
        </div>
      </Form>
      <Modal
        opener={legalDocumentOpener}
        ref={legalDocumentModalRef}
        title={
          editingLegalDocument
            ? intl.formatMessage({ id: "models.careProfile.updateLegalDocument" })
            : intl.formatMessage({ id: "models.careProfile.addNewLegalDocument" })
        }
      >
        {() => (
          <LegalDocumentForm
            journeyId={journey.id}
            kindOptions={legalDocumentKindOptions}
            legalDocument={editingLegalDocument}
            onCancel={legalDocumentOpener.close}
            onSuccess={legalDocumentOpener.close}
          />
        )}
      </Modal>
      <Modal
        opener={guardianLegalDocumentOpener}
        ref={guardianLegalDocumentModalRef}
        title={
          editingGuardianLegalDocument
            ? intl.formatMessage({ id: "models.careProfile.updateGuardianLegalDocument" })
            : intl.formatMessage({ id: "models.careProfile.addNewGuardianLegalDocument" })
        }
      >
        {() => (
          <LegalDocumentForm
            journeyId={journey.id}
            kindOptions={guardianLegalDocumentKindOptions}
            legalDocument={editingGuardianLegalDocument}
            onCancel={guardianLegalDocumentOpener.close}
            onSuccess={guardianLegalDocumentOpener.close}
          />
        )}
      </Modal>
    </>
  );
};

export default CareProfileLegalForm;
